import { apiRequest, ApiError, isTransientStatus, NetworkUnavailableError, unwrapData } from '../api/client'
import { getCached, putCached, type CachedRecord } from './db'
import { captureRequestSession } from '../auth/requestSession'

export class OfflineCacheMissError extends Error {
  constructor() {
    super('Эти данные ещё не сохранены на устройстве. Подключитесь к сети и откройте раздел снова')
    this.name = 'OfflineCacheMissError'
  }
}

export interface ResourceResult<T> {
  data: T
  source: 'network' | 'cache'
  fetchedAt: number
  stale: boolean
}

interface CollectionEnvelope {
  data: unknown[]
  meta: Record<string, unknown> & { page: number; limit: number; total: number }
  [key: string]: unknown
}

function collectionEnvelope(value: unknown): CollectionEnvelope {
  if (!value || typeof value !== 'object' || !('data' in value) || !Array.isArray(value.data) || !('meta' in value)) {
    throw new NetworkUnavailableError()
  }
  const meta = value.meta
  if (!meta || typeof meta !== 'object' || !('page' in meta) || !('limit' in meta) || !('total' in meta)
    || !Number.isSafeInteger(meta.page) || Number(meta.page) < 1
    || !Number.isSafeInteger(meta.limit) || Number(meta.limit) < 1
    || !Number.isSafeInteger(meta.total) || Number(meta.total) < 0) {
    throw new NetworkUnavailableError()
  }
  return value as CollectionEnvelope
}

function collectionPagePath(path: string, page: number): string {
  const url = new URL(path, 'https://offline.invalid')
  url.searchParams.set('page', String(page))
  return /^https?:\/\//.test(path) ? url.toString() : `${url.pathname}${url.search}${url.hash}`
}

/** Only publish a collection after every page has arrived under the same session. */
async function fetchAllPages<T>(path: string, session: ReturnType<typeof captureRequestSession>): Promise<T> {
  const raw = await apiRequest<unknown>(collectionPagePath(path, 1), { method: 'GET', rawResponse: true, authToken: session.token })
  session.assertCurrent()
  const first = collectionEnvelope(raw)
  const pageCount = Math.max(1, Math.ceil(first.meta.total / first.meta.limit))
  if (first.meta.page !== 1 || pageCount > 1000) throw new NetworkUnavailableError()

  const items: unknown[] = []
  const ids = new Set<unknown>()
  const appendPage = (envelope: CollectionEnvelope, page: number) => {
    const expectedLength = Math.min(first.meta.limit, first.meta.total - (page - 1) * first.meta.limit)
    if (envelope.meta.page !== page || envelope.meta.limit !== first.meta.limit || envelope.meta.total !== first.meta.total
      || envelope.data.length !== expectedLength) throw new NetworkUnavailableError()
    const previousCount = items.length
    for (const item of envelope.data) {
      const id = item && typeof item === 'object' && 'id' in item ? item.id : undefined
      if (id !== undefined && id !== null) {
        if (ids.has(id)) continue
        ids.add(id)
      }
      items.push(item)
    }
    // A stale endpoint returning the same records must not loop or replace a complete cache.
    if (envelope.data.length > 0 && items.length === previousCount) throw new NetworkUnavailableError()
  }
  appendPage(first, 1)
  for (let page = 2; page <= pageCount; page += 1) {
    const response = await apiRequest<unknown>(collectionPagePath(path, page), { method: 'GET', rawResponse: true, authToken: session.token })
    session.assertCurrent()
    appendPage(collectionEnvelope(response), page)
  }
  return { ...first, data: items, meta: { ...first.meta, page: 1, limit: items.length, total: first.meta.total } } as T
}

export async function getOfflineResource<T>(
  userId: string,
  cacheKey: string,
  path: string,
  maxAgeMs = 5 * 60_000,
): Promise<ResourceResult<T>> {
  const session = captureRequestSession(userId)
  const cached: CachedRecord<unknown> | null = await getCached(userId, cacheKey).catch(() => null)
  session.assertCurrent()

  if (navigator.onLine && !session.authRequired) {
    try {
      const raw = await apiRequest<unknown>(path, { method: 'GET', rawResponse: true, authToken: session.token })
      session.assertCurrent()
      await putCached(userId, cacheKey, raw).catch(() => undefined)
      return { data: unwrapData<T>(raw), source: 'network', fetchedAt: Date.now(), stale: false }
    } catch (error) {
      const canFallback = error instanceof NetworkUnavailableError || (error instanceof ApiError && isTransientStatus(error.status))
      if (!canFallback || !cached) throw error
    }
  }

  if (!cached) throw new OfflineCacheMissError()
  session.assertCurrent()
  return {
    data: unwrapData<T>(cached.value),
    source: 'cache',
    fetchedAt: cached.fetchedAt,
    stale: Date.now() - cached.fetchedAt > maxAgeMs,
  }
}

export async function getOfflineRawResource<T>(
  userId: string,
  cacheKey: string,
  path: string,
  maxAgeMs = 5 * 60_000,
  allPages = false,
): Promise<ResourceResult<T>> {
  const session = captureRequestSession(userId)
  const cached = await getCached<T>(userId, cacheKey).catch(() => null)
  session.assertCurrent()
  if (navigator.onLine && !session.authRequired) {
    try {
      const raw = allPages
        ? await fetchAllPages<T>(path, session)
        : await apiRequest<T>(path, { method: 'GET', rawResponse: true, authToken: session.token })
      session.assertCurrent()
      await putCached(userId, cacheKey, raw).catch(() => undefined)
      session.assertCurrent()
      return { data: raw, source: 'network', fetchedAt: Date.now(), stale: false }
    } catch (error) {
      const canFallback = error instanceof NetworkUnavailableError || (error instanceof ApiError && isTransientStatus(error.status))
      if (!canFallback || !cached) throw error
    }
  }
  if (!cached) throw new OfflineCacheMissError()
  session.assertCurrent()
  return { data: cached.value, source: 'cache', fetchedAt: cached.fetchedAt, stale: Date.now() - cached.fetchedAt > maxAgeMs }
}
